<template>
  <div class="content-page">
    <!-- 顶部Banner区域：始终渲染，默认使用灰色占位，避免页面上移 -->
    <PageBanner 
      :title="currentBannerTitle"
      :banner-url="currentBannerUrl"
      :loading="bannerLoading"
    />

    <!-- 主要内容区域 -->
    <div class="main-content" v-loading="loading">
      <div class="content-container">
        <!-- 左侧导航菜单 -->
        <SidebarNavigation
          :menu-title="currentMenuTitle"
          :menu-categories="menuCategories"
          :selected-category-id="selectedCategoryId"
          :expanded-subcategory-id="expandedSubcategoryId"
          @menu-click="handleMenuClick"
        />

        <!-- 右侧内容区域 -->
        <div class="content-area">
          <!-- 产品列表模式 -->
          <ProductList
            v-if="!showProductDetails"
            :products="filteredProducts"
            :loading="loading"
            @view-details="handleMenuClick"
          />

          <!-- 产品详情模式 -->
          <div v-else class="product-details-container">
            <ProductDetails 
              :product-id="selectedProductId"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Footer组件 -->
    <Footer />
  </div>
</template>

<script>
import Footer from '../components/Home/Footer.vue'
import ProductDetails from './ProductDetails.vue'
import PageBanner from '../components/PageBanner.vue'
import SidebarNavigation from '../components/SidebarNavigation.vue'
import ProductList from '../components/ProductList.vue'

export default {
  name: 'ContentPage',
  components: {
    Footer,
    ProductDetails,
    PageBanner,
    SidebarNavigation,
    ProductList
  },
  data() {
    return {
      loading: false,
      selectedCategoryId: null, // 选中的分类ID
      expandedSubcategoryId: null, // 展开的二级菜单ID
      selectedProductId: null,
      showProductDetails: false,
      menuCategories: null, // 菜单分类数据
      defaultBannerTitle: '产品服务',
      defaultBannerUrl: require('../assets/img/banner1.png'),
      currentMenuTitle: '产品服务', // 当前菜单标题
      currentMenuId: 1, // 当前菜单ID（可由路由query/params覆盖）
  menuType: 'product', // 菜单类型：product, platform, news
  lastFullTreeIdInternal: null
    };
  },
  computed: {
    filteredProducts() {
      if (!this.menuCategories || !this.menuCategories.children) {
        return [];
      }
      const list = this.menuCategories.children;
      if (!list || list.length === 0) {
        return [];
      }
      const item = list.find(ii => ii.id === this.selectedCategoryId);
      
      if (item && item.children && item.children.length > 0) {
        return item.children;
      }
      
      return [];
    },

    // 获取当前路径的节点数组（从当前节点到根节点）
    currentPathNodes() {
      if (!this.menuCategories) {
        return []
      }
      const currentId = this.selectedCategoryId
      const path = this.findPathById(currentId)
      // 直接返回路径，从当前节点到根节点（findPathById已经是从根到目标）
      return path || []
    },

    // 实时计算 Banner 标题（逐级回退：当前层级 -> 父级 -> 顶级 -> 默认/当前菜单标题）
    currentBannerTitle() {
      const nodes = this.currentPathNodes
      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i]
        if (node && node.name) return node.name
      }
      return ''
    },

    // 实时计算 Banner 图片（逐级回退：当前层级 -> 父级 -> 顶级 -> 默认图）
    currentBannerUrl() {
      const nodes = this.currentPathNodes
      for (let i = nodes.length - 1; i >= 0; i--) {
        const node = nodes[i]
        if (node && node.banner) return this.resolveImageUrl(node.banner)
      }
      return this.defaultBannerUrl
    },

    // Banner 的 loading：当还没有解析到任何层级的 banner/title 时为 true
    bannerLoading() {
      return !(this.currentBannerTitle || this.currentBannerUrl)
    }
  },
  async mounted() {
    this.determineMenuType();
    // 允许从 query.menuId / params.categoryId / params.productId 获取ID
    const incomingMenuId = this.$route.query.menuId
    const incomingRouteId = this.$route.params.categoryId || this.$route.params.productId
    const incomingAny = incomingMenuId || incomingRouteId
    if (incomingAny) {
      this.currentMenuId = parseInt(incomingAny)
    }
    await this.loadMenuCategories();
    this.handleRouteChange();
    this.updatePageTitle();
  },
  watch: {
    async '$route'(to, from) {
      this.determineMenuType();

      // 同步 currentMenuId：优先 menuId，其次 categoryId/productId
      const incomingMenuId = to.query.menuId
      const incomingRouteId = to.params.categoryId || to.params.productId
      const incomingAny = incomingMenuId || incomingRouteId
      if (incomingAny) {
        this.currentMenuId = parseInt(incomingAny)
      }

      // 仅当从 /news 切换到 /product 时，才重新请求 full-tree
      const getPrefix = (r) => {
        const p = (r && r.path) || ''
        if (typeof p !== 'string') return ''
        if (p.startsWith('/news')) return 'news'
        if (p.startsWith('/product')) return 'product'
        if (p.startsWith('/platform')) return 'platform'
        return ''
      }
      const toPrefix = getPrefix(to)
      const fromPrefix = getPrefix(from)

      if (toPrefix !== fromPrefix) {
        await this.loadMenuCategories()
      }

      // 始终根据路由调整右侧显示（列表/详情、选中项等）
      this.handleRouteChange();
      this.updatePageTitle();
    }
  },
  methods: {
    // 判断是否与当前路由相同（基于 name + params 的浅比较）
    isSameRoute(target) {
      if (!target || !target.name) return false
      if (this.$route.name !== target.name) return false
      const currentParams = this.$route.params || {}
      const targetParams = target.params || {}
      const keys = new Set([...Object.keys(currentParams), ...Object.keys(targetParams)])
      for (const k of keys) {
        if (String(currentParams[k] || '') !== String(targetParams[k] || '')) return false
      }
      return true
    },
    // 根据路由确定菜单类型
    determineMenuType() {
      const path = this.$route.path;
      if (path.startsWith('/product')) {
        this.menuType = 'product';
        this.currentMenuTitle = '产品服务';
      } else if (path.startsWith('/platform')) {
        this.menuType = 'platform';
        this.currentMenuTitle = '技术平台';
      } else if (path.startsWith('/news')) {
        this.menuType = 'news';
        this.currentMenuTitle = '新闻中心';
      } else {
        this.menuType = 'product';
        this.currentMenuTitle = '产品服务';
      }
    },

    // 处理路由变化
    handleRouteChange() {
      const categoryId = this.$route.params.categoryId;
      const productId = this.$route.params.productId;
      
      if (productId) {
        // 如果有产品ID，显示产品详情
        const itemId = parseInt(productId);
        this.selectedProductId = itemId;
        this.showProductDetails = true;
        // 通过updateExpandedSubcategory来设置正确的父级分类ID
        this.updateExpandedSubcategory(itemId);
        // 设置selectedCategoryId为产品ID（用于Banner显示）
        this.selectedCategoryId = itemId;
      } else if (categoryId) {
        // 如果有分类ID，显示产品列表
        const itemId = parseInt(categoryId);
        this.selectedCategoryId = itemId;
        this.expandedSubcategoryId = itemId;
        this.showProductDetails = false;
        this.selectedProductId = null;
      } else {
        // 如果都没有，默认选择第一个分类
        this.selectDefaultCategory();
      }
    },

    // 加载菜单分类
    async loadMenuCategories() {
      try {
        this.loading = true;
        // 选择用于 full-tree 的ID：menuId 优先，否则用路由上的分类/产品ID
      const routeId = this.$route.params.categoryId || this.$route.params.productId
      // 优先使用路由上的分类/产品ID，避免因一级菜单 menuId 造成错误或重复请求
      const fetchId = routeId || this.currentMenuId
      // 若与上次请求相同则跳过
      if (this.lastFullTreeIdInternal && Number(this.lastFullTreeIdInternal) === Number(fetchId)) {
        return
      }
        if (fetchId) {
    
          const res = await this.$http.get(`/api/menus/${encodeURIComponent(fetchId)}/full-tree`)
          if (res && res.data && res.data.success && res.data.data && res.data.data.length) {
        
            const rawData = res.data.data[0]
    
            this.menuCategories = this.normalizeMenuStructure(rawData)
          this.lastFullTreeIdInternal = Number(fetchId)

            // 成功通过 menuId 获取后，清理 URL 上的 menuId 查询参数
            if (this.$route && this.$route.query && this.$route.query.menuId) {
              const named = this.$route.name ? { name: this.$route.name, params: this.$route.params } : { path: this.$route.path }
              this.$router.replace(named)
            }
            return
          }
        }
      } catch (error) {
        console.error('加载菜单分类失败:', error);
        this.$message.error('加载菜单分类失败');
      } finally {
        this.loading = false;
      }
    },

    // 标准化菜单结构：将contents转换为children，并添加标识参数
    normalizeMenuStructure(node) {
      if (!node || typeof node !== 'object') {
        return node;
      }
      
      // 深拷贝节点，避免修改原始数据
      const normalizedNode = node.contentDetail ? {
        ...node,
        banner: node.contentDetail.imageUrl,
        title: node.contentDetail.title,
        coverImage: node.contentDetail.coverImage,
        updatedAt: node.contentDetail.updatedAt,
        subtitle: node.contentDetail.subtitle
      } : { ...node };
      
      // 如果节点有contents但没有children，将contents转换为children
      if (normalizedNode.contents && 
          normalizedNode.contents.length > 0 && 
          !normalizedNode.children) {
        normalizedNode.children = normalizedNode.contents.map(item => ({
          ...item,
          name: item.title || item.name
        }));
        // 添加标识参数，标记这是从contents转换而来的
        normalizedNode._fromContents = true;
      }
  
      // 递归处理子节点
      if (normalizedNode.children && 
          normalizedNode.children.length > 0) {
        normalizedNode.children = normalizedNode.children.map(child => this.normalizeMenuStructure(child));
      }
      
      return normalizedNode;
    },

    // 选择默认分类/详情：
    // - platform：默认进入首个子项的分类列表（/platform/:categoryId）
    // - 其他：保持原逻辑，进入首个分类（/xxx/:categoryId）
    selectDefaultCategory() {
      if (!(this.menuCategories && this.menuCategories.children && this.menuCategories.children.length > 0)) {
        return
      }
      const firstChild = this.menuCategories.children[0]
      // 平台模块与其它模块：默认进入首个分类（列表页）
      const defaultCategoryId = firstChild && firstChild.id
      if (defaultCategoryId) {
        this.selectedCategoryId = defaultCategoryId
        this.expandedSubcategoryId = defaultCategoryId
        this.selectedProductId = null
        this.showProductDetails = false
        this.navigateToCategory(defaultCategoryId)
      }
    },
    
    // 统一的菜单点击处理
    async handleMenuClick(item) {
      this.selectedCategoryId = item.id;
      
      // 根据是否有children来判断显示列表还是产品详情
      if (item.children && item.children.length > 0) {
        // 有children，显示产品列表
        this.selectedProductId = null;
        this.showProductDetails = false;
        this.expandedSubcategoryId = item.id; // 展开当前二级菜单
        this.navigateToCategory(item.id);
      } else {
        // 没有children，显示产品详情
        this.selectedProductId = item.id;
        this.showProductDetails = true;
        // 同步更新URL到详情路由
        this.navigateToProduct(item.id);
      }
    },

    // 根据产品ID更新展开的二级菜单
    updateExpandedSubcategory(productId) {
      if (!this.menuCategories || !this.menuCategories.children) {
        return;
      }
      
      const mainCategory = this.menuCategories;
      for (const subcategory of mainCategory.children) {
        if (subcategory.children && subcategory.children.length > 0) {
          for (const thirdLevel of subcategory.children) {
            if (thirdLevel.id === productId) {
              // 找到对应的三级菜单项，展开其父级二级菜单
              this.expandedSubcategoryId = subcategory.id;
              return;
            }
          }
        }
      }
    },

    // 根据菜单项ID查找菜单项
    findMenuItemById(itemId) {
      if (!this.menuCategories || !this.menuCategories.children) {
        return null;
      }
      
      const mainCategory = this.menuCategories;
      for (const subcategory of mainCategory.children) {
        if (subcategory.id === itemId) {
          return subcategory;
        }
        if (subcategory.children && subcategory.children.length > 0) {
          for (const thirdLevel of subcategory.children) {
            if (thirdLevel.id === itemId) {
              return thirdLevel;
            }
          }
        }
      }
      return null;
    },

    // 导航到指定分类
    navigateToCategory(categoryId) {
      let routeName;
      if (this.menuType === 'product') {
        routeName = 'product-category';
      } else if (this.menuType === 'platform') {
        routeName = 'platform-category';
      } else if (this.menuType === 'news') {
        routeName = 'news-category';
      } else {
        routeName = 'product-category'; // 默认
      }
      
      const target = { name: routeName, params: { categoryId: categoryId.toString() } }
      if (this.isSameRoute(target)) return
      this.$router.push(target).catch(err => {
        if (err && err.name === 'NavigationDuplicated') return
        throw err
      })
    },
    
    // 导航到指定产品详情
    navigateToProduct(productId) {
      let routeName;
      if (this.menuType === 'product') {
        routeName = 'product-detail';
      } else if (this.menuType === 'platform') {
        routeName = 'platform-detail';
      } else if (this.menuType === 'news') {
        routeName = 'news-detail';
      } else {
        routeName = 'product-detail'; // 默认
      }

      // 兼容路由要求：详情页需要 categoryId 和 productId
      const categoryId = this.getCategoryIdForProduct(productId) || this.expandedSubcategoryId || this.selectedCategoryId || productId
      const target = { name: routeName, params: { categoryId: String(categoryId), productId: String(productId) } }
      if (this.isSameRoute(target)) return
      this.$router.push(target).catch(err => {
        if (err && err.name === 'NavigationDuplicated') return
        throw err
      })
    },

    // 根据产品ID获取父级分类ID（用于拼接详情路由所需的categoryId）
    getCategoryIdForProduct(productId) {
      if (!productId || !this.menuCategories) return null
      const path = this.findPathById(Number(productId))
      if (Array.isArray(path) && path.length >= 2) {
        // path: [root, category, product]
        const parent = path[path.length - 2]
        return parent && parent.id ? parent.id : null
      }
      return null
    },
    
    // 统一解析图片URL：相对路径前加 imgserver，绝对/数据URL原样返回
    resolveImageUrl(url) {
      if (!url) return this.defaultBannerUrl
      const str = String(url)
      const lower = str.toLowerCase()
      if (lower.startsWith('http://') || lower.startsWith('https://') || lower.startsWith('data:') || lower.startsWith('//')) {
        return str
      }
      // 保证单斜杠拼接
      const base = this.imgserver || ''
      if (str.startsWith('/')) return `${base}${str}`
      return `${base}/${str}`
    },
    
    // 返回从 root 到目标节点的路径（包含 root 和目标）；找不到时仅返回 [root]
    findPathById(targetId) {
      if (!this.menuCategories) return []
      const root = this.menuCategories
      if (!targetId) return [root]

      // 使用更高效的DFS，避免频繁的数组操作
      const dfs = (node, currentPath = []) => {
        if (!node) return null
        
        const newPath = [...currentPath, node]
        
        if (node.id === targetId) return newPath
        
        if (node.children && node.children.length > 0) {
          for (const child of node.children) {
            const result = dfs(child, newPath)
            if (result) return result
          }
        }
        
        return null
      }
      
      return dfs(root) || [root]
    },

    // 动态更新页面标题
    updatePageTitle() {
      if (!this.menuCategories) return
      
      const currentId = this.selectedCategoryId || this.selectedProductId
      if (!currentId) {
        // 如果没有选中项，使用默认标题
        document.title = this.currentMenuTitle + ' - 恒毅新生物'
        return
      }
      
      // 查找当前选中项的详细信息
      const currentItem = this.findMenuItemById(currentId)
      if (currentItem && currentItem.name) {
        // 根据菜单类型和当前项名称生成标题
        let title = currentItem.name
        if (this.showProductDetails) {
          // 详情页
          // if (this.menuType === 'product') {
          //   title = title
          // } else if (this.menuType === 'platform') {
          //   title = title + ' - 技术平台详情'
          // } else if (this.menuType === 'news') {
          //   title = title + ' - 新闻详情'
          // }
        } else {
          // 列表页
          if (this.menuType === 'product') {
            title = title + ' - 产品服务'
          } else if (this.menuType === 'platform') {
            title = title + ' - 技术平台'
          } else if (this.menuType === 'news') {
            title = title + ' - 新闻中心'
          }
        }
        document.title = title + ' - 恒毅新生物'
      } else {
        // 如果找不到具体项，使用默认标题
        document.title = this.currentMenuTitle + ' - 恒毅新生物'
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.content-page {
  width: 100%;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-top: 6rem;
}

// 主要内容区域
.main-content {
  padding: 40px 0;
  
  .content-container {
    max-width: 90%;
    margin: 0 auto;
    display: flex;
    gap: 30px;
    padding: 0 20px;
  }
}

// 右侧内容区域
.content-area {
  flex: 1;
}

// 产品详情容器
.product-details-container {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  
  // 隐藏ProductDetails组件的Footer，只显示内容部分
  :deep(.product-details-page) {
    padding-top: 0;
    background-color: transparent;
    
    .product-details-content {
      margin-top: 0;
      box-shadow: none;
      border-radius: 0;
    }
    
    .footer {
      display: none;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .main-content {
    .content-container {
      flex-direction: column;
      padding: 0 10px;
    }
  }
}
</style>
